import { useState } from 'react';
import { trpc } from '@web/lib/trpc';
import { UsersTable } from './users-table';
import { ViewContainer } from '@web/components/ui/view-container';
import { UserDetailsDialog } from './user-details-dialog';
import { Filter } from '@web/components/ui/view-filters';
import { Route } from '../route';
import { UsersFilters } from '@shared/types';
import { CreateUserDialog } from './create-user-dialog';

export function UserTableView() {
  // State for pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { search, roleId }: UsersFilters = Route.useSearch();

  // Query to fetch roles for the filter dropdown
  const { data: roles } = trpc.user.listRoles.useQuery();

  const parsedRoleId = roles?.find((role) => role.name === roleId)?.roleId;

  // const { data: countData } = trpc.user.countUsers.useQuery({
  //   search,
  //   roleId: parsedRoleId,
  // });

  const { data: usersData } = trpc.user.listUsers.useQuery({
    search,
    page,
    limit: pageSize,
    roleId: parsedRoleId,
  });

  const users = usersData?.items.map((user) => ({
    ...user,
    createdAt: new Date(user.createdAt),
    updatedAt: user.updatedAt ? new Date(user.updatedAt) : null,
  }));
  const pagination = usersData?.pagination;

  // State for dialogs
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);

  const navigate = Route.useNavigate();

  // Pagination handlers
  const handlePageChange = (newPage: number) => {
    console.log('newPage', newPage);
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1); // Reset to first page when changing page size
  };

  // Filter configuration
  const filterConfig: Filter[] = [
    {
      key: 'search',
      label: 'Search',
      type: 'string',
      value: search,
      isMainFilter: true,
    },
    // For the role filter, we'll use a string filter and handle the dropdown in the component
    {
      key: 'roleId',
      label: 'Role',
      type: 'enum',
      value: roleId,
      isMainFilter: false,
      options: roles?.map((role) => role.name) ?? [],
    },
  ];

  // Handle filter changes
  function handleFilterChange(
    newSearch: Record<string, string | number | boolean | object | undefined>,
  ): void {
    setPage(1); // Reset to first page when filters change
    console.log('newSearch', newSearch);

    // Create a simplified object with string values for the router
    const searchParams: Record<string, string> = {};

    // First add current values that aren't being changed
    if (search && !('search' in newSearch)) {
      searchParams.search = search;
    }

    if (roleId && !('roleId' in newSearch)) {
      const role = roles?.find((role) => role.name === newSearch.roleId);
      const roleIdP = role?.roleId ?? '';
      searchParams.roleId = roleIdP;
    }

    // Then add the new values
    Object.entries(newSearch).forEach(([key, value]) => {
      if (value !== undefined) {
        // Skip objects, just use primitive values
        if (typeof value !== 'object') {
          searchParams[key] = value.toString();
        }
      }
    });

    // Navigate with the properly formatted search parameters
    void navigate({
      to: '/dashboard/admin/users',
      search: searchParams,
    });
  }

  return (
    <ViewContainer
      title="Users"
      addButtonLabel="Add User"
      filters={filterConfig}
      onFilterChange={handleFilterChange}
      onAddButtonClick={() => {
        setCreateUserDialogOpen(true);
      }}
    >
      {/* Users table */}
      <UsersTable
        users={users}
        pagination={{
          totalItems: pagination?.totalItems ?? 0,
          currentPage: pagination?.currentPage ?? 1,
          itemsPerPage: pagination?.itemsPerPage ?? 10,
          totalPages: pagination?.totalPages ?? 1,
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange,
        }}
        onEditUser={setSelectedUserId}
      />

      {/* User details dialog */}
      {selectedUserId && (
        <UserDetailsDialog
          userId={selectedUserId}
          open={!!selectedUserId}
          onOpenChange={(open) => {
            if (!open) setSelectedUserId(null);
          }}
        />
      )}

      {/* TODO: Add CreateUserDialog component when available */}
      {createUserDialogOpen && (
        <div>
          {/* Placeholder for CreateUserDialog */}
          <CreateUserDialog
            open={createUserDialogOpen}
            onOpenChange={setCreateUserDialogOpen}
          />
        </div>
      )}
    </ViewContainer>
  );
}
